import { useEffect, ReactNode, useMemo, useState } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import * as authTypes from "./constants/authTypes";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Unauthorised from "./components/Unauthorised";
import { getRouterBasename } from "./utils/routerBasename";

interface B2CAuthConfig {
  auth: {
    clientId: string;
    authority: string;
    knownAuthorities: string[];
    redirectUri: string;
    postLogoutRedirectUri: string;
    portalBaseUrl: string;
  };
  cache: {
    cacheLocation: "sessionStorage";
    storeAuthStateInCookie: boolean;
  };
}

interface B2CWrapperProps {
  children: ReactNode;
}

const {
  REACT_APP_CLIENT_ID,
  REACT_APP_AUTHORITY,
  REACT_APP_SIGN_IN_POLICY_ID,
  REACT_APP_KNOWN_AUTHORITIES,
  REACT_APP_CALLBACK_URL,
  REACT_APP_PORTAL_BASE_URL,
} = process.env;

if (
  !REACT_APP_CLIENT_ID ||
  !REACT_APP_AUTHORITY ||
  !REACT_APP_SIGN_IN_POLICY_ID ||
  !REACT_APP_KNOWN_AUTHORITIES ||
  !REACT_APP_CALLBACK_URL ||
  !REACT_APP_PORTAL_BASE_URL
) {
  throw new Error("Missing config");
}

const b2cAuthConfig: B2CAuthConfig = {
  auth: {
    clientId: REACT_APP_CLIENT_ID,
    authority: `${REACT_APP_AUTHORITY}/${REACT_APP_SIGN_IN_POLICY_ID}`,
    knownAuthorities: [REACT_APP_KNOWN_AUTHORITIES],
    redirectUri: REACT_APP_CALLBACK_URL,
    postLogoutRedirectUri: REACT_APP_CALLBACK_URL,
    portalBaseUrl: REACT_APP_PORTAL_BASE_URL,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

const ProtectedContent = ({ children }: B2CWrapperProps) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const { login } = useAuth();

  useEffect(() => {
    if (!isAuthenticated && inProgress === "none") {
      login();
    }
  }, [isAuthenticated, inProgress]);

  if (!isAuthenticated && inProgress !== "none") {
    return <></>;
  }

  return <>{children}</>;
};

const B2CWrapper = ({ children }: B2CWrapperProps) => {
  const [initialized, setInitialized] = useState(false);
  const basename = getRouterBasename();
  const msalInstance = useMemo(() => {
    return new PublicClientApplication(b2cAuthConfig);
  }, []);

  useEffect(() => {
    msalInstance.initialize().then(() => {
      setInitialized(true);
    });
  }, [msalInstance]);

  if (!initialized) {
    return <></>;
  }

  return (
    <BrowserRouter basename={basename}>
      <MsalProvider instance={msalInstance}>
        <AuthProvider authType={authTypes.B2C}>
          <Routes>
            <Route path="/unauthorised" element={<Unauthorised />} />
            <Route path="/" element={<ProtectedContent>{children}</ProtectedContent>} />
          </Routes>
        </AuthProvider>
      </MsalProvider>
    </BrowserRouter>
  );
};

export default B2CWrapper;
